import type React from 'react'
import type { ReactNode } from 'react'
import ReportProblemRounded from '@mui/icons-material/ReportProblemRounded'
import { AppDialog } from './AppDialog'
import { getNlsString } from '../../nls/nlsRuntime'

export type ConfirmActionModalProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  body: string
  confirmLabel: string
  cancelLabel?: string
  danger?: boolean
  busy?: boolean
  confirmDisabled?: boolean
  icon?: ReactNode
  children?: ReactNode
}

/**
 * Reusable confirmation modal for destructive or irreversible actions
 * (such as removing a member/helper, revoking a Field Pass, or self-leaving).
 *
 * Built on AppDialog to adhere to repo-wide modal styles and Escape-to-close handling.
 */
export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel = getNlsString('NLSCommonActionCancel'),
  danger = false,
  busy = false,
  confirmDisabled = false,
  icon,
  children,
}) => {
  const resolvedIcon = icon !== undefined ? icon : danger ? <ReportProblemRounded /> : undefined

  return (
    <AppDialog
      open={open}
      size="sm"
      data-testid="confirm-action-modal"
      title={title}
      message={body}
      icon={resolvedIcon}
      tone={danger ? 'danger' : undefined}
      onDismiss={onClose}
      actions={[
        {
          label: cancelLabel,
          variant: 'ghost',
          onClick: onClose,
          disabled: busy,
        },
        {
          label: busy ? '' : confirmLabel,
          variant: danger ? 'danger' : 'primary',
          onClick: onConfirm,
          disabled: busy || confirmDisabled,
          loading: busy,
        },
      ]}
    >
      {children}
    </AppDialog>
  )
}
